// Purchasing → New purchase (F-CTRL-N75). Two ways to buy stock, one place:
// order from a supplier (the PO flow) or record something already bought and
// paid for. "Already bought" replaced the Market Buys tabs; it records one
// received purchase order and writes the money once, by who paid.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShoppingBasket, Truck } from 'lucide-react'
import apiClient from '@/api/client'
import type { Supplier } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatMoney } from '@/lib/currency'
import { getStoredUserRole } from '@/lib/staff-roles'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import type { MarketDeliverTo, MarketPaidBy } from '@/lib/inventoryFlow/marketTypes'
import { addMarketLine, marketBody, marketPayersFor, marketPlacesFor, marketProblem, marketTotal, type MarketDraftLine } from '@/lib/inventoryFlow/marketLogic'
import { businessTodayIso } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { BoughtItemsEditor, ChoiceRow, type BillPhoto } from './BoughtItemsEditor'

type ToastFn = (type: 'success' | 'error', message: string) => void

const STALL = '__stall__'

/** The item a Restock / Record a purchase button was pressed on. */
export interface BoughtStartItem {
  id: string
  name: string
  unit: string
  tracks_expiry?: boolean
  shelf_life_days?: number | null
  /** Pre-fills the price paid (last paid, else average cost). */
  unit_cost?: number | null
}

/** Order from a supplier, or already bought & paid? */
export function NewPurchaseChoiceDialog({ open, onOpenChange, onOrder, onBought }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOrder: () => void
  onBought: () => void
}) {
  const card = 'flex w-full items-start gap-3 rounded-lg border-2 border-border bg-card p-4 text-left hover:bg-muted/50 touch-manipulation'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{BT.chooseTitle}</DialogTitle>
          <DialogDescription>{BT.chooseHelp}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button type="button" className={card} onClick={() => { onOpenChange(false); onOrder() }}>
            <Truck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <span><span className="block font-semibold">{BT.orderTitle}</span><span className="text-sm text-muted-foreground">{BT.orderHelp}</span></span>
          </button>
          <button type="button" className={card} onClick={() => { onOpenChange(false); onBought() }}>
            <ShoppingBasket className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <span><span className="block font-semibold">{BT.boughtTitle}</span><span className="text-sm text-muted-foreground">{BT.boughtHelp}</span></span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Record stock already bought and paid for. */
export function AlreadyBoughtDialog({ open, onOpenChange, suppliers, showToast, onRecorded, startItem }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  suppliers: Supplier[]
  showToast: ToastFn
  onRecorded?: (purchaseOrderId: string) => void
  /** Pilot Batch 5: opened from an item's Restock — start with it on the list. */
  startItem?: BoughtStartItem | null
}) {
  const qc = useQueryClient()
  const role = getStoredUserRole()
  const places = marketPlacesFor(role)
  // Pilot Batch 5: petty cash first (no open day needed); the till only for
  // an admin or a manager — the server refuses anyone else.
  const payers = marketPayersFor(role)
  const today = businessTodayIso(useDayBoundaryHour())
  const [deliverTo, setDeliverTo] = useState<MarketDeliverTo>('store')
  const [paidBy, setPaidBy] = useState<MarketPaidBy>(payers[0])
  const [vendor, setVendor] = useState(STALL)
  const [stall, setStall] = useState('')
  const [buyer, setBuyer] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<MarketDraftLine[]>([])
  const [photos, setPhotos] = useState<BillPhoto[]>([])
  const [key, setKey] = useState(newIdempotencyKey)

  useEffect(() => {
    if (!open) return
    setDeliverTo(places[0] ?? 'store')
    setPaidBy(payers[0])
    setVendor(STALL)
    setStall('')
    setBuyer('')
    setNotes('')
    setLines(startItem
      ? addMarketLine([], startItem, 1, today).map((l) => ({ ...l, unit_cost: startItem.unit_cost != null && startItem.unit_cost > 0 ? String(startItem.unit_cost) : '' }))
      : [])
    setPhotos([])
    setKey(newIdempotencyKey())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const vendors = suppliers.filter((s) => s.is_active && !s.is_consignment)
  const problem = marketProblem(lines, paidBy, buyer)
  const total = marketTotal(lines)
  const save = useMutation({
    mutationFn: () => apiClient.recordMarketPurchase(marketBody({
      deliverTo, paidBy, buyer, notes, lines, photos,
      supplier: vendor === STALL ? stall : '',
      supplierId: vendor === STALL ? undefined : vendor,
    }), key),
    onSuccess: (res) => {
      showToast('success', BT.saved(formatMoney(res.data?.total ?? total)))
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['expenses'] })
      onOpenChange(false)
      if (res.data?.id) onRecorded?.(res.data.id)
    },
    onError: (e: Error) => showToast('error', e.message.split(' — ')[0] || 'Could not record the purchase'),
  })

  if (places.length === 0) return null
  return (
    <Dialog open={open} onOpenChange={(o) => !save.isPending && onOpenChange(o)}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b px-6 py-4 pr-12">
          <DialogTitle>{BT.title}</DialogTitle>
          <DialogDescription>{BT.help}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <ChoiceRow label={BT.deliverTo} value={deliverTo} options={places.map((p) => [p, BT.places[p]] as const)} onChange={setDeliverTo} />
          <BoughtItemsEditor lines={lines} onLines={setLines} photos={photos} onPhotos={setPhotos} onError={(m) => showToast('error', m)} />
          <div className="space-y-1.5">
            <ChoiceRow label={BT.paidBy} value={paidBy} options={payers.map((p) => [p, BT.payers[p]] as const)} onChange={setPaidBy} />
            <p className="text-sm text-muted-foreground">{BT.payerHelp[paidBy]}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {paidBy === 'buyer' && (
              <label className="space-y-1 sm:col-span-2"><span className="text-sm font-medium">{BT.buyer}</span>
                <Input maxLength={100} value={buyer} onChange={(e) => setBuyer(e.target.value)} /></label>
            )}
            <label className="space-y-1"><span className="text-sm font-medium">{BT.vendor}</span>
              <Select value={vendor} onValueChange={setVendor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={STALL}>{BT.vendorStall}</SelectItem>
                  {vendors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            {vendor === STALL && (
              <label className="space-y-1"><span className="text-sm font-medium">{BT.supplier}</span>
                <Input maxLength={150} placeholder={BT.supplierPlaceholder} value={stall} onChange={(e) => setStall(e.target.value)} /></label>
            )}
            <label className="space-y-1 sm:col-span-2"><span className="text-sm font-medium">{BT.notes}</span>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
          </div>
          {problem && lines.length > 0 && <p className="text-sm text-destructive">{BT.problems[problem]}</p>}
        </div>
        <DialogFooter className="shrink-0 items-center gap-2 border-t px-6 py-4 sm:justify-between">
          <span className="text-base font-bold tabular-nums">{BT.total} {formatMoney(total)}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>{BT.cancel}</Button>
            <Button disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>{save.isPending ? BT.saving : BT.save}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
